import React, { useMemo } from 'react';

import { Divider, FormControl, FormLabel, Grid, GridProps, MenuItem, Select } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import ControlledInput from '../../../../../components/forms/ControlledInput';
import { StyledLoadingButton } from '../../../../../components/forms/LoadingButton';
import ClassContainer from '../../../ClassModal/ClassContainer';
import Advanced from '../../../ClassModal/Sections/Advanced';
import Dates from '../../../ClassModal/Sections/Dates';
import { OrganizationType, useSchoolInfo } from '../../../../../hooks/useSchoolInfo';

export const RequiredIcon = () => (
    <Typography component="span" color="error">
        *
    </Typography>
);

const DateContainer = styled('div')`
    max-width: 635px;
    margin: 0 -1.5rem;
`;

const AdvancedContainer = styled('div')`
    margin: 1rem -0.75rem;
`;

type ClassDetailEditFormProps = {
    onSubmit: () => void;
};

const FormControlGrid = (props: GridProps) => <Grid container sx={{ mt: 2 }} {...props}>{props.children}</Grid>;

export const ClassDetailEditForm = (props: ClassDetailEditFormProps) => {
    const { onSubmit } = props;
    const { t } = useTranslation(['create_edit_class', 'portal']);
    const { classRecord, control, updateClassStatus } = useContainer(ClassContainer);

    const mainInstructor = useMemo(() => `${classRecord?.first_name} ${classRecord?.last_name}`, [classRecord]);

    const { data: school } = useSchoolInfo();

    const renderInstructorInfo = () => {
        if (school && school.type === OrganizationType.Teams) {
            return null;
        }

        return (
            <>
                <Typography fontWeight="bold">{t('portal:classes-page.lead-instructor')}</Typography>
                <Typography>{t('portal:classes-page.lead-instructor-description')}</Typography>

                <Box sx={{ mt: 3 }}>
                    <FormControl size="small">
                        <FormLabel>{t('portal:classes-page.lead-instructor')}</FormLabel>
                        <Select value={mainInstructor} disabled>
                            <MenuItem value={mainInstructor}>{mainInstructor}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Divider sx={{ mt: 3 }}/>
            </>
        );
    };

    return (
        <form onSubmit={onSubmit}>
            {renderInstructorInfo()}

            <FormControlGrid spacing={2}>
                <Grid item xs={12} md={6} lg={4}>
                    <FormControl fullWidth>
                        <ControlledInput type="text" name="className" control={control}
                            label={<><RequiredIcon/> {t('create_edit_class:details.class-name')}</>}
                        />
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <FormControl fullWidth>
                        <ControlledInput type="text" name="classId" control={control} label={t('create_edit_class:details.class-id')} />
                    </FormControl>
                </Grid>
            </FormControlGrid>

            <FormControlGrid>
                <Grid item xs={12} md={8} lg={6}>
                    <FormControl fullWidth>
                        <ControlledInput type="text" multiline name="description" control={control} label={t('create_edit_class:details.description')} />
                    </FormControl>
                </Grid>
            </FormControlGrid>

            <FormControlGrid>
                <Grid item xs={12} md={6} lg={4}>
                    <FormControl fullWidth>
                        <ControlledInput type="text" inputProps={{ maxLength: 10 }} name="classPasscode" control={control}
                            label={<><RequiredIcon/> {t('create_edit_class:details.class-passcode')}</>} />
                    </FormControl>
                </Grid>
            </FormControlGrid>

            <Divider sx={{ mt: 3 }}/>

            <DateContainer>
                <Dates/>
            </DateContainer>

            <Divider sx={{ my: 2 }}/>

            <AdvancedContainer>
                <Advanced/>
            </AdvancedContainer>

            <div>
                <StyledLoadingButton loading={updateClassStatus === 'LOADING'} color="primary" variant="contained" type="submit">{t('create_edit_class:save')}</StyledLoadingButton>
            </div>
        </form>
    );
};
